/**
 * Where an invite link lands.
 *
 * The agreed flow: open link → sign in (the auth gate handles that, and this
 * route is reachable only once signed in) → SEE the group → tap join →
 * location is checked → in range joins, out of range is told why.
 *
 * The screen deliberately shows the group to people who cannot join it. An
 * invitation that resolves to "not found" is indistinguishable from a broken
 * link, and someone two streets away should be told they are close rather than
 * wrong. What it never shows is who is inside: the API's invite view carries a
 * member COUNT and no identities, because a link can be forwarded anywhere.
 */
import Ionicons from "@expo/vector-icons/Ionicons";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ActivityIndicator, Pressable, ScrollView, Text, View } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { useTranslation } from "react-i18next";

import { ApiError, groupApi } from "@/api";
import { Radius, Spacing, type ThemeColors } from "@/constants/theme";
import { useTheme } from "@/hooks/use-theme";
// The two join labels live under `home` because that is where the same choice
// is first offered (the browse list's join sheet). One home for one pair of
// words, imported here rather than duplicated with a second key.
import { homeStrings } from "@/i18n/features/home.i18n";
import { socialStrings as strings } from "@/i18n/features/social.i18n";
import { formatRadiusM } from "@/lib/nearby-radius";
import type { SmallGroupInvite } from "@/types";

export default function GroupInviteScreen() {
  const { t } = useTranslation();
  const router = useRouter();
  const c = useTheme();
  const styles = useMemo(() => createStyles(c), [c]);
  const { id } = useLocalSearchParams<{ id: string }>();

  const [invite, setInvite] = useState<SmallGroupInvite | null>(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [missing, setMissing] = useState(false);
  /**
   * A join refused by the SERVER, which outranks whatever the preview said.
   * The preview is a snapshot: someone can walk out of range, or the last seat
   * can go, between opening the link and pressing the button. The server is
   * the only authority on whether a join happened.
   */
  const [refused, setRefused] = useState<SmallGroupInvite["block"] | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setInvite(await groupApi.invite(id));
      setMissing(false);
    } catch (error) {
      // 404 is an ended or deleted group; anything else is a transient
      // failure. Both land on a message rather than an empty screen.
      setMissing(error instanceof ApiError && error.status === 404);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * @param allowMemberCues Whether the people in this group may cue the joiner.
   *   The intent rides on the join itself: a separate follow-up call would
   *   leave a window in which somebody who chose Cue Me is in the group with
   *   the permission off, and would make the OFF state depend on a request
   *   having been made rather than on a choice.
   */
  async function join(allowMemberCues: boolean) {
    if (!id || joining) return;
    setJoining(true);
    setRefused(null);
    try {
      await groupApi.join(id, { allowMemberCues });
      // Straight into the thread — the invitation is spent, and coming back
      // to a "join" button they have already used reads as a failure.
      router.replace(`/group/${id}`);
    } catch (error) {
      // Re-read rather than trusting the local snapshot: the reason may have
      // changed under us, and the fresh preview carries the right message.
      const fresh = await groupApi.invite(id).catch(() => null);
      if (fresh) setInvite(fresh);
      setRefused(
        fresh?.block ??
          (error instanceof ApiError && error.status === 403
            ? "out_of_range"
            : null),
      );
    } finally {
      setJoining(false);
    }
  }

  const block = refused ?? invite?.block ?? null;

  if (loading) {
    return (
      <SafeAreaView style={styles.safe}>
        <View style={styles.centered}>
          <ActivityIndicator color={c.tint} />
        </View>
      </SafeAreaView>
    );
  }

  if (missing || !invite) {
    return (
      <SafeAreaView style={styles.safe}>
        <View style={styles.centered}>
          <Text style={styles.title}>{t(strings.inviteMissingTitle)}</Text>
          <Text style={styles.body}>{t(strings.inviteMissingBody)}</Text>
          <Pressable
            onPress={() => router.replace("/home")}
            accessibilityRole="button"
            style={styles.secondaryBtn}
          >
            <Text style={styles.secondaryText}>{t(strings.inviteBrowse)}</Text>
          </Pressable>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safe}>
      <ScrollView contentContainerStyle={styles.body_}>
        <View style={styles.card}>
          <Text style={styles.eyebrow}>{t(strings.inviteEyebrow)}</Text>
          <Text style={styles.title}>{invite.name}</Text>
          <Text style={styles.vibe}>{invite.vibe}</Text>

          <View style={styles.metaRow}>
            <Ionicons name="people-outline" size={16} color={c.textMuted} />
            <Text style={styles.meta}>
              {t(strings.inviteMembers, {
                count: invite.memberCount,
                max: invite.maxMembers,
              })}
            </Text>
          </View>
          <View style={styles.metaRow}>
            <Ionicons name="navigate-outline" size={16} color={c.textMuted} />
            <Text style={styles.meta}>
              {t(strings.inviteRange, { distance: formatRadiusM(invite.radiusM) })}
            </Text>
          </View>

          {invite.isMember ? (
            <Pressable
              onPress={() => router.replace(`/group/${invite.id}`)}
              accessibilityRole="button"
              style={styles.primaryBtn}
            >
              <Text style={styles.primaryText}>{t(strings.inviteOpen)}</Text>
            </Pressable>
          ) : block ? (
            <View style={styles.blockPanel}>
              <Text style={styles.blockTitle}>{t(blockTitleKey(block))}</Text>
              <Text style={styles.blockBody}>{t(blockBodyKey(block))}</Text>
              {/* Retry, not a dead end: the reason is usually "right now". */}
              <Pressable
                onPress={() => void load()}
                accessibilityRole="button"
                style={styles.secondaryBtn}
              >
                <Text style={styles.secondaryText}>
                  {t(strings.inviteTryAgain)}
                </Text>
              </Pressable>
            </View>
          ) : (
            /*
              Two joins, not a join plus a checkbox — the same choice the browse
              list's sheet offers, and for the same reason. A checkbox beside a
              Join button is the shape people tap past: its default silently
              becomes the answer for everybody who did not read it. Two labelled
              buttons make the choice the act of joining.
            */
            <>
              <Text style={styles.joinHint}>{t(homeStrings.joinCueHint)}</Text>
              <Pressable
                onPress={() => void join(true)}
                disabled={joining}
                accessibilityRole="button"
                accessibilityLabel={t(homeStrings.joinAndCue)}
                style={[styles.primaryBtn, joining && styles.btnBusy]}
              >
                {joining ? (
                  <ActivityIndicator color="#fff" size="small" />
                ) : (
                  <Text style={styles.primaryText}>
                    {t(homeStrings.joinAndCue)}
                  </Text>
                )}
              </Pressable>
              <Pressable
                onPress={() => void join(false)}
                disabled={joining}
                accessibilityRole="button"
                accessibilityLabel={t(homeStrings.joinOnly)}
                style={[styles.secondaryBtn, joining && styles.btnBusy]}
              >
                <Text style={styles.secondaryText}>
                  {t(homeStrings.joinOnly)}
                </Text>
              </Pressable>
            </>
          )}

          <Pressable
            onPress={() => router.replace("/home")}
            accessibilityRole="button"
            style={styles.linkBtn}
          >
            <Text style={styles.linkText}>{t(strings.inviteBrowse)}</Text>
          </Pressable>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

/**
 * Every block has copy of its own. Collapsing them into one "you can't join"
 * would tell someone standing outside the range the same thing as someone
 * whose location is switched off, and only one of those is fixable by walking.
 */
function blockTitleKey(block: NonNullable<SmallGroupInvite["block"]>) {
  switch (block) {
    case "out_of_range":
      return strings.inviteOutOfRangeTitle;
    case "full":
      return strings.inviteFullTitle;
    case "ended":
      return strings.inviteEndedTitle;
    case "not_visible":
      return strings.inviteNotVisibleTitle;
    case "removed":
      return strings.inviteRemovedTitle;
    case "no_location":
    default:
      return strings.inviteNoLocationTitle;
  }
}

function blockBodyKey(block: NonNullable<SmallGroupInvite["block"]>) {
  switch (block) {
    case "out_of_range":
      return strings.inviteOutOfRangeBody;
    case "full":
      return strings.inviteFullBody;
    case "ended":
      return strings.inviteEndedBody;
    case "not_visible":
      return strings.inviteNotVisibleBody;
    case "removed":
      return strings.inviteRemovedBody;
    case "no_location":
    default:
      return strings.inviteNoLocationBody;
  }
}

function createStyles(c: ThemeColors) {
  return {
    safe: { flex: 1, backgroundColor: c.background } as const,
    centered: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      padding: Spacing.lg,
      gap: Spacing.sm,
    } as const,
    body_: { padding: Spacing.lg, gap: Spacing.md } as const,
    card: {
      backgroundColor: c.surface,
      borderColor: c.border,
      borderWidth: 1,
      borderRadius: Radius.lg,
      padding: Spacing.lg,
      gap: Spacing.sm,
    } as const,
    eyebrow: {
      color: c.tint,
      fontSize: 12,
      fontWeight: "800",
      textTransform: "uppercase",
      letterSpacing: 0.6,
    } as const,
    title: { color: c.text, fontSize: 22, fontWeight: "800" } as const,
    vibe: { color: c.textMuted, fontSize: 15 } as const,
    body: { color: c.textMuted, fontSize: 15, textAlign: "center" } as const,
    metaRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: Spacing.xs,
    } as const,
    meta: { color: c.textMuted, fontSize: 14 } as const,
    blockPanel: {
      backgroundColor: c.background,
      borderColor: c.border,
      borderWidth: 1,
      borderRadius: Radius.md,
      padding: Spacing.md,
      gap: Spacing.xs,
      marginTop: Spacing.sm,
    } as const,
    blockTitle: { color: c.text, fontSize: 16, fontWeight: "800" } as const,
    blockBody: { color: c.textMuted, fontSize: 14, lineHeight: 20 } as const,
    primaryBtn: {
      backgroundColor: c.tint,
      borderRadius: Radius.md,
      paddingVertical: 14,
      alignItems: "center",
      marginTop: Spacing.sm,
    } as const,
    primaryText: { color: "#fff", fontSize: 15, fontWeight: "800" } as const,
    btnBusy: { opacity: 0.7 } as const,
    // Sits above the two join buttons and explains what the choice between them
    // means. Muted, because it is the sentence you read once and then stop
    // reading — the buttons themselves have to carry the decision.
    joinHint: {
      color: c.textMuted,
      fontSize: 13,
      lineHeight: 18,
      marginTop: Spacing.sm,
    } as const,
    secondaryBtn: {
      borderColor: c.border,
      borderWidth: 1,
      borderRadius: Radius.md,
      paddingVertical: 12,
      alignItems: "center",
      marginTop: Spacing.sm,
    } as const,
    secondaryText: { color: c.text, fontSize: 14, fontWeight: "700" } as const,
    linkBtn: { alignItems: "center", paddingVertical: Spacing.sm } as const,
    linkText: { color: c.tint, fontSize: 14, fontWeight: "700" } as const,
  };
}
